import { useCallback, useEffect, useState } from "react";
import {
  COMMISSION_PERCENT_DEFAULTS,
  getConfig,
  parseFcMotivatePercent,
} from "services/config.service";
import { getCommissionDashboardSummary } from "services/finance.service";
import { subscribeCommissionUpdated } from "utils/commissionEvents";

const EMPTY_SOURCE_FACET = {
  accruals: [],
  totalAccrued: 0,
  totalPaid: 0,
  totalUnpaid: 0,
  attributedRevenue: 0,
  percentage: null,
  count: 0,
};

const EMPTY_SUMMARY = {
  periodMonth: "",
  totalOwedThisMonth: 0,
  totalPaidThisMonth: 0,
  fcMotivate: null,
  fcMotivatePercentage: null,
  emailManager: { ...EMPTY_SOURCE_FACET },
  saleTeam: { ...EMPTY_SOURCE_FACET },
  aceProgress: null,
  commissionLog: [],
};

export default function useCommissionDashboard(api) {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const [data, config] = await Promise.all([
        getCommissionDashboardSummary(api),
        getConfig(api).catch(() => null),
      ]);
      const next = data ?? EMPTY_SUMMARY;
      const percentages = config ?? COMMISSION_PERCENT_DEFAULTS;

      setSummary({
        ...EMPTY_SUMMARY,
        ...next,
        emailManager: {
          ...EMPTY_SOURCE_FACET,
          ...(next.emailManager ?? {}),
          percentage:
            next.emailManager?.percentage ?? percentages.emailManagerPercentage,
        },
        saleTeam: {
          ...EMPTY_SOURCE_FACET,
          ...(next.saleTeam ?? {}),
          percentage:
            next.saleTeam?.percentage ?? percentages.saleTeamPercentage,
        },
        aceProgress: next.aceProgress
          ? {
              ...next.aceProgress,
              percentage:
                next.aceProgress.percentage ??
                percentages.acePerformancePercentage,
            }
          : null,
        fcMotivatePercentage: parseFcMotivatePercent(
          next.fcMotivate?.percentage ?? percentages.fcMotivatePercentage
        ),
      });
    } catch {
      setSummary(EMPTY_SUMMARY);
      setError("Failed to load commission data. Please try again.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return subscribeCommissionUpdated(() => {
      load({ silent: true });
    });
  }, [load]);

  return { summary, isLoading, error, retry: load };
}
